// DERIVADO DE: gestion-web-cliente@1a5ce2d39
//   src/app/auxiliares/servicios/http/{eventos-genericos,materiales-servicio-tecnico,
//   listado-categorias,permisos}.service.ts
// CAMBIOS: `@Service()`, `HttpClient` directo, y sólo los métodos que usa el
//   servicio técnico. Las RUTAS no se tocan (ADR-008).

import { HttpClient } from '@angular/common/http';
import { Service, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import type {
  IEventoGenerico,
  IListado,
  IListadoCategoria,
  IMaterialServicioTecnico,
  IPermiso,
  IQueryParam,
  IRol,
} from 'modelos';

import { queryParams } from '@core/datos/query-params';
import { SIN_NOTIFICAR_ERROR, contexto } from '@core/http/contexto';

/**
 * Las solicitudes de servicio técnico.
 *
 * **Una solicitud es un evento genérico**, no una entidad aparte: `filtrador` la
 * separa (`'Servicio Técnico'` o `'Verificación'`), `tipoEvento` dice de qué es
 * —acá siempre `'Evento Técnico Luminaria'`— y `detallesTecnicos` guarda a quién
 * se le asignó y desde cuándo la puede tratar.
 *
 * Suena raro y es correcto: el operador ve en la misma cronología el evento que
 * abrió la luminaria y la visita que lo resolvió.
 */
@Service()
export class SolicitudesHttp {
  private readonly http = inject(HttpClient);
  private readonly ruta = '/eventos-genericos';

  /** `silencioso`: si falla, no se avisa al usuario (para los sondeos de fondo). */
  listar(
    query?: IQueryParam,
    opciones?: { silencioso?: boolean },
  ): Promise<IListado<IEventoGenerico>> {
    return firstValueFrom(
      this.http.get<IListado<IEventoGenerico>>(this.ruta, {
        params: queryParams(query),
        context: opciones?.silencioso ? contexto(SIN_NOTIFICAR_ERROR) : undefined,
      }),
    );
  }

  porId(id: string): Promise<IEventoGenerico> {
    return firstValueFrom(this.http.get<IEventoGenerico>(`${this.ruta}/${id}`));
  }

  crear(dato: Partial<IEventoGenerico>): Promise<IEventoGenerico> {
    return firstValueFrom(this.http.post<IEventoGenerico>(this.ruta, dato));
  }

  actualizar(id: string, dato: Partial<IEventoGenerico>): Promise<IEventoGenerico> {
    return firstValueFrom(this.http.put<IEventoGenerico>(`${this.ruta}/${id}`, dato));
  }

  eliminar(id: string): Promise<void> {
    return firstValueFrom(this.http.delete<void>(`${this.ruta}/${id}`));
  }

  /**
   * Asignar un técnico.
   *
   * Tiene ruta propia y **no se hace con un `update`**: además de escribir el
   * id, el backend pasa la solicitud a `Asignado` y le manda el push al
   * teléfono del técnico. Guardar el campo a mano deja la solicitud asignada
   * para la pantalla y sin avisarle a nadie.
   *
   * Con `idTecnico: null` la **desasigna**: es la misma ruta, como en IRIX.
   */
  asignar(
    id: string,
    idTecnico: string | null,
    fechaDisponibleParaTratar?: string,
  ): Promise<IEventoGenerico> {
    return firstValueFrom(
      this.http.put<IEventoGenerico>(`${this.ruta}/asignar/${id}`, {
        idTecnico,
        fechaDisponibleParaTratar,
      }),
    );
  }
}

/**
 * Los materiales.
 *
 * La misma colección guarda tres cosas distintas, y `tipo` las separa:
 * **usado** y **pedido** cuelgan de una solicitud; **asignado** no tiene
 * solicitud y es el stock que tiene el técnico en la caja. El stock puede además
 * apuntar a una entidad del sistema (`idEntidad`), que es como el técnico tiene
 * nodos disponibles para un cambio.
 */
@Service()
export class MaterialesHttp {
  private readonly http = inject(HttpClient);
  private readonly ruta = '/materiales-servicio-tecnico';

  /** `silencioso`: si falla, no se avisa al usuario (para los sondeos de fondo). */
  listar(
    query?: IQueryParam,
    opciones?: { silencioso?: boolean },
  ): Promise<IListado<IMaterialServicioTecnico>> {
    return firstValueFrom(
      this.http.get<IListado<IMaterialServicioTecnico>>(this.ruta, {
        params: queryParams(query),
        context: opciones?.silencioso ? contexto(SIN_NOTIFICAR_ERROR) : undefined,
      }),
    );
  }

  crear(dato: Partial<IMaterialServicioTecnico>): Promise<IMaterialServicioTecnico> {
    return firstValueFrom(this.http.post<IMaterialServicioTecnico>(this.ruta, dato));
  }

  actualizar(
    id: string,
    dato: Partial<IMaterialServicioTecnico>,
  ): Promise<IMaterialServicioTecnico> {
    return firstValueFrom(this.http.put<IMaterialServicioTecnico>(`${this.ruta}/${id}`, dato));
  }

  eliminar(id: string): Promise<void> {
    return firstValueFrom(this.http.delete<void>(`${this.ruta}/${id}`));
  }
}

/**
 * Los técnicos.
 *
 * Salen de `/permisos/tecnicos`: técnico no es un tipo de usuario sino un
 * **permiso con la función Técnico**. La misma persona puede ser técnica de un
 * municipio y operadora de otro.
 */
@Service()
export class TecnicosHttp {
  private readonly http = inject(HttpClient);

  listar(query?: IQueryParam): Promise<IListado<IPermiso>> {
    return firstValueFrom(
      this.http.get<IListado<IPermiso>>('/permisos/tecnicos', { params: queryParams(query) }),
    );
  }

  // Alta, edición y baja van por `/permisos`, como en IRIX: un técnico es un
  // permiso más. Las mismas rutas que `PermisosHttp` de Administración, que un
  // dominio no puede importar; el backend exige las acciones de Administración
  // (`Crear/Editar/Eliminar usuarios`), no las de Servicio Técnico.

  crear(dato: Partial<IPermiso>): Promise<IPermiso> {
    return firstValueFrom(this.http.post<IPermiso>('/permisos', dato));
  }

  actualizar(id: string, dato: Partial<IPermiso>): Promise<IPermiso> {
    return firstValueFrom(this.http.put<IPermiso>(`/permisos/${id}`, dato));
  }

  /**
   * Quitarle el permiso de técnico. El backend, además, libera su stock
   * (`tipo: 'asignado'`) y desasigna sus solicitudes, que vuelven a Pendiente.
   */
  eliminar(id: string): Promise<void> {
    return firstValueFrom(this.http.delete<void>(`/permisos/${id}`));
  }

  /** Los roles con la función Técnico que el operador puede ver (`/rols`). */
  roles(): Promise<IListado<IRol>> {
    return firstValueFrom(
      this.http.get<IListado<IRol>>('/rols', {
        params: queryParams({
          filter: JSON.stringify({ funciones: 'Técnico' }),
          select: 'nombre global idCliente funciones',
          populate: JSON.stringify([{ path: 'cliente', select: 'nombre' }]),
          sort: 'nombre',
          unlimited: true,
          includeChildren: true,
        }),
      }),
    );
  }
}

/**
 * Los títulos posibles de una solicitud.
 *
 * No son código: son datos que el cliente carga (`listadocategorias` con
 * `categoria: 'Evento-Tecnico-Luminaria'`). El título importa porque hay uno que
 * dispara comportamiento: **"Cambio de nodo"** es el único con el que el técnico
 * puede proponer reemplazar el nodo de la luminaria.
 */
@Service()
export class TitulosSolicitudHttp {
  private readonly http = inject(HttpClient);

  listar(query?: IQueryParam): Promise<IListado<IListadoCategoria>> {
    return firstValueFrom(
      this.http.get<IListado<IListadoCategoria>>('/listado-categorias', {
        params: queryParams(query),
      }),
    );
  }
}
